import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  Row,
  FormGroup,
  Spinner,
  Label,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import {
  useGetEquipmentUsedTypeQuery,
  useCreateEquipmentUsedTypeMutation,
  useGetEquipmentUsedQuestionnaireKeyQuery,
  useGetEquipmentUsedQuestionnaireKeyTypeByIdQuery,
  useCreateEquipmentUsedQuestionnaireKeyMutation,
  useUpdateEquipmentUsedQuestionnaireKeyByIdMutation,
} from "slices/pageSettings/pageSettingsSlice";

interface Equipment {
  id: number;
  name: string;
  description: string;
  question_count: number;
}

interface Questionnaire {
  id: number;
  question: string;
  equipment_id: number;
}

interface QuestionModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (data: Questionnaire) => void;
  initialData?: Questionnaire | null;
  loading: boolean;
}

const QuestionModal: React.FC<QuestionModalProps> = ({
  isOpen,
  toggle,
  onSave,
  initialData,
  loading,
}) => {
  const [question, setQuestion] = useState<string>(initialData?.question || "");

  useEffect(() => {
    setQuestion(initialData?.question || "");
  }, [initialData]);

  const handleSave = () => {
    if (question.trim()) {
      const questionData: Questionnaire = {
        id: initialData?.id || 0,
        question: question,
        equipment_id: initialData?.equipment_id || 0,
      };
      onSave(questionData);
    }
  };

  const handleCancel = () => {
    setQuestion("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {initialData ? "Update" : "Create"} Question
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="question">Question</Label>
          <Input
            type="textarea"
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            autoComplete="off"
            disabled={loading}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={loading || question.trim() === ""}
        >
          {loading ? <Spinner size="sm" /> : "Save"}
        </Button>
        <Button color="secondary" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const EquipmentUsedQuestionnaire = () => {
  // Equipment states
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    name: "",
    description: "",
  });

  // Questionnaire states
  const [activeEquipmentId, setActiveEquipmentId] = useState<
    number | undefined
  >(undefined);
  //   const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal states
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<Questionnaire | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  // API calls
  const { data: equipmentData, refetch: refetchEquipment } =
    useGetEquipmentUsedTypeQuery(undefined);
  const [createEquipment] = useCreateEquipmentUsedTypeMutation();

  const { data: questionnaireData, refetch: refetchQuestionnaire } =
    useGetEquipmentUsedQuestionnaireKeyTypeByIdQuery(activeEquipmentId, {
      skip: activeEquipmentId === undefined,
    });

  console.log("questionnaireData", questionnaireData);

  const [createQuestion] = useCreateEquipmentUsedQuestionnaireKeyMutation();
  const [updateQuestion] = useUpdateEquipmentUsedQuestionnaireKeyByIdMutation();

  // Handlers
  const handleAddEquipmentModalToggle = () => {
    setIsAddEquipmentModalOpen(!isAddEquipmentModalOpen);
    setNewEquipment({ name: "", description: "" });
  };

  const handleAddEquipmentSubmit = async () => {
    try {
      await createEquipment({
        name: newEquipment.name,
        description: newEquipment.description,
      }).unwrap();
      refetchEquipment();
      handleAddEquipmentModalToggle();
    } catch (error) {
      console.error("Error adding equipment:", error);
    }
  };

  const toggleQuestionModal = () => setQuestionModalOpen(!questionModalOpen);

  const handleSaveQuestion = async (questionData: Questionnaire) => {
    setLoading(true);
    try {
      if (questionData.id) {
        await updateQuestion({
          id: questionData.id,
          updateData: questionData,
        }).unwrap();
      } else {
        await createQuestion({
          equipment_used_type: activeEquipmentId || 0,
          question: questionData.question,
        }).unwrap();
      }
      refetchQuestionnaire();
      toggleQuestionModal();
    } catch (error) {
      console.error("Error saving question:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewQuestion = () => {
    setCurrentQuestion(null);
    toggleQuestionModal();
  };

  const handleEditQuestion = (question: Questionnaire) => {
    setCurrentQuestion(question);
    toggleQuestionModal();
  };

  //   const filteredQuestions = questionnaireData?.filter((q: Questionnaire) =>
  //     q.question.toLowerCase().includes(searchTerm.toLowerCase())
  //   ) || [];

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <div className="d-flex align-items-center mb-4">
            <h5 className="card-title flex-grow-1 mb-0">
              Equipment & Questionnaire
            </h5>
            <Button color="primary" onClick={handleAddEquipmentModalToggle}>
              <i className="ri-add-line me-1 align-bottom"></i> Add Equipment
            </Button>
          </div>

          <Row>
            {/* Equipment Navigation */}
            <Col md={3}>
              <Nav pills vertical className="flex-column" id="v-pills-tab">
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({
                      "mb-2": true,
                      active: activeEquipmentId === undefined,
                    })}
                    onClick={() => setActiveEquipmentId(undefined)}
                  >
                    All Equipment
                  </NavLink>
                </NavItem>

                {equipmentData?.map((equipment: Equipment) => (
                  <NavItem key={equipment.id}>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={classnames({
                        "mb-2": true,
                        active: activeEquipmentId === equipment.id,
                      })}
                      onClick={() => setActiveEquipmentId(equipment.id)}
                    >
                      {equipment.name}{" "}
                      <span className="badge bg-primary-subtle text-primary me-1">
                        {equipment.question_count}
                      </span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Col>

            {/* Main Content */}
            <Col md={9}>
              <TabContent
                activeTab={
                  activeEquipmentId === undefined ? "all" : activeEquipmentId
                }
                className="text-muted mt-4 mt-md-0"
              >
                {/* All Equipment View */}
                <TabPane tabId="all">
                  <h5>All Equipment</h5>
                  <Table striped>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentData?.map(
                        (equipment: Equipment, index: number) => (
                          <tr key={equipment.id}>
                            <td>{index + 1}</td>
                            <td>{equipment.name}</td>
                            <td>{equipment.description}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </TabPane>

                <hr />

                {/* Equipment-specific Questionnaire View */}
                {equipmentData?.map((equipment: Equipment) => (
                  <TabPane tabId={equipment.id} key={equipment.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <h5>{equipment.name} Questionnaire</h5>
                        <p>
                          <b>Description:</b>{" "}
                          <span className="text-muted">
                            {equipment.description}
                          </span>
                        </p>
                      </div>

                      <div className="mb-4">
                        <Button
                          color="secondary"
                          onClick={handleAddNewQuestion}
                          size="sm"
                        >
                          <i className="ri-add-line me-1 align-bottom"></i> Add
                          Question
                        </Button>
                      </div>
                    </div>

                    {/* Questions List */}
                    <Table striped>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Question</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questionnaireData?.length > 0 ? (
                          questionnaireData?.map(
                            (question: Questionnaire, index: number) => (
                              <tr key={question.id}>
                                <td>{index + 1}</td>
                                <td>{question.question}</td>
                                <td>
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleEditQuestion(question)}
                                  >
                                    Edit
                                  </Button>
                                </td>
                              </tr>
                            ),
                          )
                        ) : (
                          <tr>
                            <td colSpan={3} className="text-center">
                              {questionnaireData?.length
                                ? "No questions match your search"
                                : "No questions added yet for this equipment"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPane>
                ))}
              </TabContent>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Add Equipment Modal */}
      <Modal
        isOpen={isAddEquipmentModalOpen}
        toggle={handleAddEquipmentModalToggle}
      >
        <ModalHeader toggle={handleAddEquipmentModalToggle}>
          Add New Equipment
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="equipment-name">Name</Label>
            <Input
              type="text"
              id="equipment-name"
              value={newEquipment.name}
              onChange={(e) =>
                setNewEquipment({ ...newEquipment, name: e.target.value })
              }
              placeholder="Enter equipment name"
            />
          </FormGroup>
          <FormGroup>
            <Label for="equipment-description">Description</Label>
            <Input
              type="textarea"
              id="equipment-description"
              value={newEquipment.description}
              onChange={(e) =>
                setNewEquipment({
                  ...newEquipment,
                  description: e.target.value,
                })
              }
              placeholder="Enter equipment description"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleAddEquipmentModalToggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddEquipmentSubmit}
            disabled={!newEquipment.name.trim()}
          >
            Add Equipment
          </Button>
        </ModalFooter>
      </Modal>

      {/* Question Modal */}
      <QuestionModal
        isOpen={questionModalOpen}
        toggle={toggleQuestionModal}
        onSave={handleSaveQuestion}
        initialData={currentQuestion}
        loading={loading}
      />
    </React.Fragment>
  );
};

export default EquipmentUsedQuestionnaire;
